import { useCallback, useState, type FormEvent } from 'react';
import { feedbackApi } from '../services/FeedbackApiService';

const MAX_FEEDBACK_MESSAGE_LENGTH = 2000;
const MAX_FEEDBACK_EMAIL_LENGTH = 254;

type SubmitState = 'idle' | 'submitting' | 'sent';

export function FeedbackWidget(): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [submitState, setSubmitState] = useState<SubmitState>('idle');
  const [error, setError] = useState<string | null>(null);

  const resetForm = useCallback((): void => {
    setMessage('');
    setEmail('');
    setError(null);
    setSubmitState('idle');
  }, []);

  const handleClose = useCallback((): void => {
    setIsOpen(false);
    resetForm();
  }, [resetForm]);

  const handleOpen = useCallback((): void => {
    setIsOpen(true);
    setError(null);
  }, []);

  const handleSubmit = useCallback(async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    const trimmedMessage = message.trim();
    if (!trimmedMessage) {
      setError('Feedback message is required');
      return;
    }

    setSubmitState('submitting');
    setError(null);
    try {
      await feedbackApi.submitFeedback(trimmedMessage, email);
      setSubmitState('sent');
      setMessage('');
      setEmail('');
    } catch (submitError) {
      setSubmitState('idle');
      setError(submitError instanceof Error ? submitError.message : 'Failed to submit feedback');
    }
  }, [email, message]);

  if (!isOpen) {
    return (
      <button
        type="button"
        onClick={handleOpen}
        className="fixed bottom-5 right-5 z-40 flex h-12 w-12 items-center justify-center rounded-full bg-blue-600 text-white shadow-lg transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-950"
        aria-label="Open feedback form"
        title="Feedback"
      >
        <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
          <path
            d="M21 11.5a8.4 8.4 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.4 8.4 0 0 1-3.8-.9L3 21l1.9-5.7A8.4 8.4 0 0 1 4 11.5a8.5 8.5 0 0 1 17 0Z"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
          />
        </svg>
      </button>
    );
  }

  return (
    <section className="fixed bottom-5 right-4 z-40 w-[calc(100vw-2rem)] max-w-sm rounded-lg border border-gray-200 bg-white shadow-xl dark:border-gray-800 dark:bg-gray-900">
      <div className="flex items-center justify-between gap-3 border-b border-gray-200 px-4 py-3 dark:border-gray-800">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Feedback</h2>
        <button
          type="button"
          onClick={handleClose}
          className="flex h-8 w-8 items-center justify-center rounded-md text-gray-500 transition hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-white"
          aria-label="Close feedback form"
          title="Close"
        >
          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
            <path d="M6 6l12 12M18 6 6 18" strokeLinecap="round" strokeWidth={2} />
          </svg>
        </button>
      </div>

      {submitState === 'sent' ? (
        <div className="space-y-4 px-4 py-4">
          <p className="text-sm font-medium text-green-700 dark:text-green-300">Feedback sent.</p>
          <button
            type="button"
            onClick={handleClose}
            className="w-full rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Done
          </button>
        </div>
      ) : (
        <form onSubmit={(event) => void handleSubmit(event)} className="space-y-4 px-4 py-4">
          <div>
            <label htmlFor="feedback-message" className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Message
            </label>
            <textarea
              id="feedback-message"
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              maxLength={MAX_FEEDBACK_MESSAGE_LENGTH}
              rows={5}
              className="mt-1 w-full resize-none rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-950 dark:text-white"
              required
            />
          </div>

          <div>
            <label htmlFor="feedback-email" className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Email optional
            </label>
            <input
              id="feedback-email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              maxLength={MAX_FEEDBACK_EMAIL_LENGTH}
              className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-950 dark:text-white"
            />
          </div>

          {error && (
            <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300">
              {error}
            </p>
          )}

          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={handleClose}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-100 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitState === 'submitting'}
              className="rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitState === 'submitting' ? 'Sending...' : 'Send'}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
